import React, { useEffect, useMemo, useState } from "react";
import { capitalizer } from "../../../../../../cp/utils";
import { getRegistrationFields } from "../../../../utils";
import { TYPE_HYBRID } from "../../../../constants/common";
import { FIELD_KONTUR_FOCUS, INPUT_TYPE_DISCLAIMER, PARTICIPATION_TYPE } from "../../../../constants/components/authentication";
import { participationTypes } from "../../../../models/components/AuthenticationModels";
import getCapitalizedLocale from "../../../../helpers/get-capitalized-locale";
import { getUserInfo } from "../../../../helpers/user";
import { updateClientRegistration } from "../../../../utils/api";
import { useLocaleContext } from "../../../../context/LocaleContext";
import { useUserContext } from "../../../../context/UserContext";
import { useModalContext } from "../../../../context/ModalContext";
import InputField from "../../../Authentication/InputField";
import UserProfileDeleteModal from "../UserProfileDeleteModal";
import { UserAvatar } from "../..";
import { CButton } from "../../../UI";

import './style.scss';
import InputKonturFocus from "../../../Authentication/InputKonturFocus";
import { USER_TYPE_ANONYMOUS } from "../../../../constants/user";

const UserProfileInfo = ({ pageSetting, event }) => {
    const $locale = useLocaleContext();
    const { user } = useUserContext();
    const { renderModal } = useModalContext();
    const { participationFormatLabels } = event.moduleAuth;

    const [info, setInfo] = useState(() => getUserInfo(user, event.registrationFields));
    const [errors, setErrors] = useState({});
    const [isEditMode, setEditMode] = useState(false);

    const participationTypeField = {
        label: $locale.get('auth.login.participationType'),
        type: 'select',
        name: PARTICIPATION_TYPE,
        required: true,
        options: (!!participationFormatLabels && !!participationFormatLabels.length)
            ? participationFormatLabels.map((format) => ({
                value: format.value,
                label: format['label' + getCapitalizedLocale($locale.name)],
            }))
            : participationTypes($locale),
    };

    const fields = useMemo(() => {
        let _fields = getRegistrationFields(event, $locale.getProperty);
        _fields = _fields.filter(({type}) => type !== INPUT_TYPE_DISCLAIMER);

        if (event.participationType === TYPE_HYBRID) {
            _fields = [participationTypeField, ..._fields]
        }

        return _fields;
    }, []);

    useEffect(() => {
        setInfo((prevState) => ({
            ...prevState,
            ...getUserInfo(user, fields, user.participationType),
        }));
    }, [user]);

    const handleChangeInfo = (fieldName) => {
        if (fieldName === PARTICIPATION_TYPE) return;

        return (value) => {
            setInfo({ ...info, [fieldName]: value });
            setErrors(({ [fieldName]: _, ...errors }) => errors);
        };
    };

    const handleSetError = ({fieldName, required}) => {
        if (!required) return;

        const value = info[fieldName] || null;

        if (!info[fieldName] || (typeof value === 'string' && !value.trim()) || !value) {
            setErrors({ ...errors, [fieldName]: { hasError: true, name: !value ? '' : fieldName } });
        }
    };

    const handleSaveInfo = async() => {
        if (!!Object.keys(errors).length) {
            return;
        }

        try {
            await updateClientRegistration(event.eventKey, info);
            setEditMode(false);
        } catch (error) {
            console.log(error);
        }
    };

    // Удаление пользователя будет реализовано в рамках другой задачи
    // const handleDeleteUser = () => {
    //     renderModal(
    //         UserProfileDeleteModal,
    //         {
    //             $locale,
    //             onRemove: () => {},
    //         },
    //     );
    // };

    return (
        <div className="user-profile-info page-block">
            <div className="user-profile-info__header">
                <h1
                    className="page-block-title"
                    style={{
                        fontSize: !!pageSetting?.titleSize ? pageSetting?.titleSize : '',
                        fontWeight: pageSetting?.titleBold ? 'bold' : 'normal',
                    }}
                >
                    {pageSetting
                        ? pageSetting[`title${capitalizer($locale.name)}`]
                        : $locale.getProperty(page, 'title')
                    }
                </h1>
                {user?.type !== USER_TYPE_ANONYMOUS && (
                    <div className="user-profile-info__header-actions">
                        {isEditMode
                            ? <CButton
                                size="md-x2"
                                icon="check"
                                theme="white"
                                label={$locale.get('registration.addPhoto.save')}
                                onClick={handleSaveInfo}
                                />
                            : <CButton
                                size="md-x2"
                                blank
                                label={$locale.get('registration.addPhoto.edit')}
                                onClick={() => setEditMode(true)}
                            />
                        }
                        {/* <CButton
                            size="md-x2"
                            blank
                            onClick={handleDeleteUser}
                            icon="trash"
                        /> */}
                    </div>
                )}
            </div>
            <div className="user-profile-info__block">
                <UserAvatar user={user} />
                <form className="user-profile-info__form">
                    {event.moduleAuth.email &&(
                        <div>
                            <InputField
                                $locale={$locale}
                                label={$locale.get('auth.login.email.email')}
                                type="email"
                                name="email"
                                value={user.email}
                                disabled
                                readOnly
                                required
                            />
                        </div>
                    )}
                    <InputField
                        $locale={$locale}
                        label={$locale.get('auth.login.email.name')}
                        type="text"
                        name="name"
                        value={info?.name}
                        disabled={!isEditMode}
                        required
                        onChange={handleChangeInfo('name')}
                        onBlur={() => handleSetError({fieldName: 'name', required: true})}
                    />
                    {fields?.map((field) => (
                        field.type === FIELD_KONTUR_FOCUS
                            ? <InputKonturFocus
                                {...field}
                                key={field.name}
                                eventKey={event.eventKey}
                                $locale={$locale}
                                value={info?.[field.name]}
                                disabled={!isEditMode}
                                readOnly={!isEditMode}
                                error={errors[field.name]?.hasError}
                                errorName={''}
                                onChange={handleChangeInfo(field.name)}
                                onBlur={() => handleSetError({fieldName: field.name, required: field.required})}
                            />
                            : <InputField
                                {...field}
                                key={field.name}
                                $locale={$locale}
                                value={info?.[field.name]}
                                disabled={!isEditMode || field.name === PARTICIPATION_TYPE}
                                defaultOptionTitle={$locale.get('input.select.defaultTitle')}
                                error={errors[field.name]?.hasError}
                                errorName={''}
                                isOptionsDisabled={field.name === PARTICIPATION_TYPE}
                                onChange={handleChangeInfo(field.name)}
                                onBlur={() => handleSetError({fieldName: field.name, required: field.required})}
                            />
                    ))}
                </form>
            </div>
        </div>
    );
};

export default UserProfileInfo;
